import { Card, Typography, Button, Space, Tooltip, Switch } from "antd";
import { Pie, Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  Title as ChartTitle,
} from "chart.js";
import { ExpandOutlined, PictureOutlined, FileExcelOutlined, BarChartOutlined, PieChartOutlined } from "@ant-design/icons";
import { useState, useRef } from "react";
import dayjs from "dayjs";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, ChartTooltip, Legend, ChartDataLabels, ChartTitle);

const { Title } = Typography;

const SEXO_CONFIG = {
  M: { label: "Masculino", color: "#024589", textColor: "#fff" },
  F: { label: "Femenino",  color: "#E91E8C", textColor: "#fff" },
  O: { label: "Otro",      color: "#F4A000", textColor: "#000" },
};

const getConfig = (key) =>
  SEXO_CONFIG[key] ?? { label: key, color: "#B4B4B4", textColor: "#000" };

export function SexoChart({ data = [] }) {
  const [chartType, setChartType] = useState("pie");
  const [viewOption, setViewOption] = useState("count");
  const [isExpanded, setIsExpanded] = useState(false);
  const chartRef = useRef(null);

  if (!data || data.length === 0) {
    return (
      <Card hoverable style={{ borderRadius: 12 }}>
        <Title level={5}>Distribución por Sexo</Title>
        <p>No hay datos disponibles.</p>
      </Card>
    );
  }

  const total      = data.reduce((s, d) => s + d.total, 0);
  const labels     = data.map((d) => getConfig(d.sexo_titular).label);
  const values     = data.map((d) => d.total);
  const colors     = data.map((d) => getConfig(d.sexo_titular).color);
  const textColors = data.map((d) => getConfig(d.sexo_titular).textColor);
  const pctValues  = data.map((d) => +((d.total / total) * 100).toFixed(1));

  const displayValues = viewOption === "count" ? values : pctValues;

  const pieData = {
    labels,
    datasets: [{ data: displayValues, backgroundColor: colors, borderWidth: 2, borderColor: "#fff" }],
  };

  const barData = {
    labels,
    datasets: [{
      label: viewOption === "count" ? "Personas" : "Porcentaje",
      data: displayValues,
      backgroundColor: colors,
      borderColor: colors,
      borderWidth: 1,
      borderRadius: 6,
    }],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { usePointStyle: true, padding: 20 } },
      tooltip: {
        callbacks: {
          label: (ctx) => viewOption === "count"
            ? ` ${values[ctx.dataIndex].toLocaleString()} (${pctValues[ctx.dataIndex]}%)`
            : ` ${pctValues[ctx.dataIndex]}%`,
        },
      },
      datalabels: {
        color: textColors,
        font: { weight: "bold", size: 13 },
        formatter: (v) => viewOption === "count" ? v.toLocaleString() : `${v}%`,
      },
    },
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => viewOption === "count"
            ? ` ${values[ctx.dataIndex].toLocaleString()} (${pctValues[ctx.dataIndex]}%)`
            : ` ${pctValues[ctx.dataIndex]}%`,
        },
      },
      datalabels: {
        anchor: "center",
        align: "center",
        color: (ctx) => textColors[ctx.dataIndex],
        font: { weight: "bold", size: 13 },
        formatter: (v) => viewOption === "count" ? v.toLocaleString() : `${v}%`,
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.05)" }, ticks: { callback: (v) => Number(v).toLocaleString() } },
    },
  };

  const exportImage = () => {
    if (!chartRef.current) return;
    const canvas = chartRef.current.canvas;
    const exp = document.createElement("canvas");
    const ctx = exp.getContext("2d");
    exp.width = canvas.width * 2; exp.height = canvas.height * 2;
    ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, exp.width, exp.height);
    ctx.scale(2, 2); ctx.drawImage(canvas, 0, 0);
    const link = document.createElement("a");
    link.href = exp.toDataURL("image/png", 1.0);
    link.download = `distribucion_sexo_${dayjs().format("YYYY-MM-DD")}.png`;
    link.click();
  };

  const exportCSV = () => {
    const rows = [
      ["Sexo", "Total", "Porcentaje"],
      ...data.map((d) => [getConfig(d.sexo_titular).label, d.total, ((d.total / total) * 100).toFixed(2) + "%"]),
      ["TOTAL", total, "100%"],
    ];
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `distribucion_sexo_${dayjs().format("YYYY-MM-DD")}.csv`;
    link.click(); URL.revokeObjectURL(link.href);
  };

  return (
    <Card
      hoverable
      style={{
        borderRadius: 12,
        ...(isExpanded && { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, margin: 20, backgroundColor: "white" }),
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Distribución por Sexo</Title>
          <div style={{ fontSize: 14, color: "#666", marginTop: 4 }}>{`${total.toLocaleString()} encuestados totales`}</div>
        </div>
        <Space wrap>
          <Tooltip title="Cambiar entre cantidad y porcentaje">
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 12px", backgroundColor: "#f8f9fa", borderRadius: 6, border: "1px solid #dee2e6" }}>
              <span style={{ fontSize: 13, color: viewOption === "count" ? "#024589" : "#6c757d", fontWeight: viewOption === "count" ? 600 : "normal" }}>Cantidad</span>
              <Switch checked={viewOption === "percentage"} onChange={(c) => setViewOption(c ? "percentage" : "count")} size="small" />
              <span style={{ fontSize: 13, color: viewOption === "percentage" ? "#024589" : "#6c757d", fontWeight: viewOption === "percentage" ? 600 : "normal" }}>Porcentaje</span>
            </div>
          </Tooltip>
          <Space>
            <Tooltip title="Cambiar tipo de gráfico">
              <Button icon={chartType === "pie" ? <BarChartOutlined /> : <PieChartOutlined />} onClick={() => setChartType(chartType === "pie" ? "bar" : "pie")} size="small">
                {chartType === "pie" ? "Barras" : "Pastel"}
              </Button>
            </Tooltip>
            <Tooltip title="Exportar imagen">
              <Button icon={<PictureOutlined />} onClick={exportImage} size="small" type="primary">Imagen</Button>
            </Tooltip>
            <Tooltip title="Exportar CSV">
              <Button icon={<FileExcelOutlined />} onClick={exportCSV} size="small">CSV</Button>
            </Tooltip>
            <Tooltip title={isExpanded ? "Minimizar" : "Maximizar"}>
              <Button icon={<ExpandOutlined />} onClick={() => setIsExpanded(!isExpanded)} size="small" type={isExpanded ? "primary" : "default"} />
            </Tooltip>
          </Space>
        </Space>
      </div>

      <div style={{ height: isExpanded ? "calc(100vh - 180px)" : 350, backgroundColor: "#fff" }}>
        {chartType === "pie"
          ? <Pie ref={chartRef} data={pieData} options={pieOptions} />
          : <Bar ref={chartRef} data={barData} options={barOptions} />}
      </div>
    </Card>
  );
}
